import { isNil } from 'lodash';
import { useEffect } from 'react';
import { useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { ApiError } from '../../services/api-error.model';
import { fetchArticle } from '../../services/articles/articles.service';
import { AccessStatus, ArticleDto } from '../../services/dtos/article/article.dto';
import usePreviousValue from '../common/usePreviousValue';
import { queryKeys } from '../queryKeyFactory';
import useArticleUpdate from './useArticleUpdate';

export default (
  articleId: number | null,
  confirmQuotaUse: boolean = true,
  confirmLoanWaiver: boolean = true
): UseQueryResult<ArticleDto | undefined, ApiError> => {
  const queryClient = useQueryClient();
  const prevConfirmQuotaUse = usePreviousValue(confirmQuotaUse);
  const prevConfirmLoanWaiver = usePreviousValue(confirmLoanWaiver);
  const { send } = useArticleUpdate();

  const query = useQuery<ArticleDto | undefined, Error>(
    queryKeys.fetchArticles.singleById(articleId),
    async () => {
      if (!articleId) {
        return undefined;
      }
      const articleDto = await fetchArticle(articleId, confirmQuotaUse, confirmLoanWaiver);

      const quota = !confirmQuotaUse || articleDto.quota?.usageExceeded ? articleDto.quota : null;
      return { ...articleDto, quota };
    },
    {
      enabled: !isNil(articleId),
      onSuccess: (articleDto: ArticleDto | undefined) => {
        if (articleDto) {
          send(articleDto);

          if (
            [AccessStatus.loanWaiverConfirmRequired, AccessStatus.quotaUsageConfirmRequired].includes(
              articleDto.accessStatus
            )
          ) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.fetchArticles.singleById(articleId),
              refetchActive: false,
            });
          }
        }
      },
    }
  );
  const { isStale, refetch } = query;

  // Auto refetch stale article query when confirmQuotaUse or confirmLoanWaiver is changed from false to true
  useEffect(() => {
    if (
      isStale &&
      ((prevConfirmQuotaUse === false && confirmQuotaUse) || (prevConfirmLoanWaiver === false && confirmLoanWaiver))
    ) {
      refetch();
    }
  }, [isStale, confirmQuotaUse, confirmLoanWaiver]);

  return query;
};
